import * as React from "react";
import { useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import { NavMenuOption, PageLayout } from "components/PageLayout/PageLayout";
import { ClientsService, Client, ApiError, ClientConfig, CustomFieldDefinitionCategory } from "gen/clients";
import { ApiErrorMessage } from "../../../components/ApiErrorMessage/ApiErrorMessage";
import { useDialogState } from "../../../hooks/useDialogState";
import { GeneralInformationTab } from "./tabs/GeneralInformationTab/GeneralInformationTab";
import { CustomFieldsTab } from "./tabs/CustomFieldsTab/CustomFieldsTab";
import { Alert, Box, Paper, Snackbar, Stack, Tab, Tabs, Toolbar } from "@mui/material";
import { PageLoading } from "../../../components/PageLoading/PageLoading";
import { ClientContactsTab } from "./tabs/ClientContactsTab/ClientContactsTab";
import { WorkflowSettingsTab } from "./tabs/WorkflowSettingsTab/WorkflowSettingsTab";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { LanguageGroupsTab } from "./tabs/LanguageGroupsTab/LanguageGroupsTab";
import { PagePath } from "../../../types/PagePath";
import { ApplicationsTab } from "./tabs/ApplicationsTab/ApplicationsTab";
import { ApiKeysTab } from "./tabs/ApiKeysTab/ApiKeysTab";
import { EventSubscriptionsTab } from "./tabs/EventSubscriptionsTab/EventSubscriptionsTab";
import { WebhooksTab } from "./tabs/WebhooksTab/WebhooksTab";
import { AiPromptsTab } from "./tabs/AiPromptsTab/AiPromptsTab";
import { ClientLockProvider } from "contexts/ClientLockContext";
import { useIsSuperUser } from "hooks/useIsSuperUser";
import { ClientLockedWarning } from "./components/ClientLockedWarning";

interface PathParams {
  clientId: string;
  tabId?: string;
}

enum TabId {
  general = "general",
  projectCustomFields = "projectCustomFields",
  userCustomFields = "userCustomFields",
  applications = "applications",
  clientContacts = "clientContacts",
  workflows = "workflows",
  languageGroups = "languageGroups",
  apiKeys = "apiKeys",
  eventSubscriptions = "eventSubscriptions",
  webhooks = "webhooks",
  aiPrompts = "aiPrompts"
}

const ClientDetailsPage: React.FC = () => {
  const history = useHistory();
  const { clientId, tabId } = useParams<PathParams>();
  const [successToastVisible, showSuccessToast, closeSuccessToast] = useDialogState();
  const [syncToastVisible, showSyncToast, closeSyncToast] = useDialogState();
  const [selectedTabId, setSelectedTabId] = React.useState<string | null>(tabId || TabId.general);

  const {
    data: client,
    isLoading: isClientLoading,
    isError: isClientError,
    error: clientError
  } = useQuery<Client, ApiError>(["getClient", clientId], {
    queryFn: () => ClientsService.getClient({ clientId }),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: 0
  });

  const {
    data: existingClientConfig,
    isLoading: isClientConfigLoading,
    isError: isClientConfigError,
    error: clientConfigError,
    refetch: refetchClientConfig
  } = useQuery<ClientConfig, ApiError>(["getClientConfig", clientId], {
    queryFn: () => ClientsService.getClientConfig({ id: clientId }),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    cacheTime: 0
  });

  const {
    mutate: syncClientConfigWithXtrf,
    isLoading: isSyncLoading,
    error: xtrfSyncError
  } = useMutation({
    mutationFn: (clientId: string) => ClientsService.syncClientConfigWithXtrf({ id: clientId }),
    onSuccess: () => {
      showSyncToast();
      refetchClientConfig();
    }
  });

  const onSaveSuccess = React.useCallback(() => {
    showSuccessToast();
    refetchClientConfig();
  }, [showSuccessToast, refetchClientConfig]);

  const clientConfig = React.useMemo<ClientConfig>(
    () => existingClientConfig || { id: clientId },
    [existingClientConfig, clientId]
  );
  const isLoading = isClientLoading || isClientConfigLoading;
  const isError = isClientError || (isClientConfigError && clientConfigError?.status !== 404);
  const error = clientError || clientConfigError;

  const isSuperUser = useIsSuperUser();
  const isConfigLocked = !!clientConfig.isConfigLocked;
  const isReadOnly = isConfigLocked && !isSuperUser;

  return (
    <>
      {!!xtrfSyncError && <SnackbarApiError error={xtrfSyncError} />}
      <Snackbar open={successToastVisible} autoHideDuration={10000} onClose={closeSuccessToast}>
        <Alert onClose={closeSuccessToast} severity="success" sx={{ width: "100%" }}>
          Settings saved successfully
        </Alert>
      </Snackbar>
      <Snackbar open={syncToastVisible} autoHideDuration={10000} onClose={closeSyncToast}>
        <Alert onClose={closeSyncToast} severity="success" sx={{ width: "100%" }}>
          Synchronization with XTRF succeeded
        </Alert>
      </Snackbar>

      <PageLayout
        title="Client Details"
        selectedNavMenu={NavMenuOption.CLIENTS}
        breadcrumbs={[
          {
            label: "Clients",
            path: "/clients"
          },
          {
            label: "Client Details",
            active: true
          }
        ]}
      >
        {isLoading && <PageLoading />}
        {isError && error && <ApiErrorMessage apiError={error} />}
        {!isLoading && !isError && client && clientConfig && (
          <ClientLockProvider isConfigLocked={isConfigLocked} isReadOnly={isReadOnly}>
            <div>
              <Toolbar disableGutters={true} variant="dense">
                <Box flexGrow={1}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <h3 style={{ margin: 0 }}>
                      {client.id} - {clientConfig.companyName}
                    </h3>
                    <ClientLockedWarning />
                  </Stack>
                </Box>
                <Stack direction="row" spacing={1}>
                  <LoadingButton
                    variant="outlined"
                    color="primary"
                    onClick={() => syncClientConfigWithXtrf(client.id)}
                    isLoading={isSyncLoading}
                    disabled={isReadOnly}
                  >
                    Sync with Xtrf
                  </LoadingButton>
                </Stack>
              </Toolbar>
              <Paper sx={{ p: 2 }}>
                <Tabs
                  variant="scrollable"
                  value={selectedTabId || undefined}
                  onChange={(event, value) => {
                    setSelectedTabId(value);
                    history.push(`${PagePath.clients}/${clientId}/${value}`);
                  }}
                >
                  <Tab value={TabId.general} label="General Information" />
                  <Tab value={TabId.applications} label="Applications" />
                  <Tab value={TabId.workflows} label="Workflows" />
                  <Tab value={TabId.projectCustomFields} label="Project Custom Fields" />
                  <Tab value={TabId.userCustomFields} label="User Custom Fields" />
                  <Tab value={TabId.clientContacts} label="Client Contacts" />
                  <Tab value={TabId.languageGroups} label="Language Groups" />
                  <Tab value={TabId.apiKeys} label="API Keys" />
                  <Tab value={TabId.eventSubscriptions} label="Event Subscriptions" />
                  <Tab value={TabId.webhooks} label="Webhooks" />
                  <Tab value={TabId.aiPrompts} label="AI Prompts" />
                </Tabs>
                <Box sx={{ mt: 2 }}>
                  {selectedTabId === TabId.general && (
                    <GeneralInformationTab client={client} clientConfig={clientConfig} onSaveSuccess={onSaveSuccess} />
                  )}
                  {selectedTabId === TabId.workflows && (
                    <WorkflowSettingsTab client={client} onSaveSuccess={onSaveSuccess} />
                  )}
                  {selectedTabId === TabId.projectCustomFields && (
                    <CustomFieldsTab
                      clientId={clientId}
                      category={CustomFieldDefinitionCategory.PROJECT}
                      onSaveSuccess={onSaveSuccess}
                    />
                  )}
                  {selectedTabId === TabId.userCustomFields && (
                    <CustomFieldsTab
                      clientId={clientId}
                      category={CustomFieldDefinitionCategory.USER}
                      onSaveSuccess={onSaveSuccess}
                    />
                  )}
                  {selectedTabId === TabId.clientContacts && <ClientContactsTab clientId={clientId} />}
                  {selectedTabId === TabId.applications && (
                    <ApplicationsTab clientConfig={clientConfig} onSaveSuccess={onSaveSuccess} />
                  )}
                  {selectedTabId === TabId.languageGroups && (
                    <LanguageGroupsTab clientConfig={clientConfig} onSaveSuccess={onSaveSuccess} />
                  )}
                  {selectedTabId === TabId.apiKeys && <ApiKeysTab clientId={clientId} onSaveSuccess={onSaveSuccess} />}
                  {selectedTabId === TabId.eventSubscriptions && <EventSubscriptionsTab clientId={clientId} />}
                  {selectedTabId === TabId.webhooks && <WebhooksTab clientId={clientId} />}
                  {selectedTabId === TabId.aiPrompts && (
                    <AiPromptsTab clientConfig={clientConfig} onSaveSuccess={onSaveSuccess} />
                  )}
                </Box>
              </Paper>
            </div>
          </ClientLockProvider>
        )}
      </PageLayout>
    </>
  );
};

export { ClientDetailsPage };
